import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { least } from 'd3-array';
import { get, find, isNumber, map, isEmpty } from 'lodash';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const CARD_MARGIN = 5;
const TOP_MARGIN = 15;
const LEFT_MARGIN = 100;
const BOTTOM_MARGIN = 65;
const RIGHT_MARGIN = 100;
const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'simple-multi-line-chart';

class MultiLineChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  drawChart = () => {
    const {
      alpha,
      colorRange,
      dataNamed,
      dataNumeric,
      height,
      isCard,
      numericName,
      globalProperties,
      selectedElements,
      weight,
      width
    } = this.props;

    const margin = {
      top: isCard ? CARD_MARGIN : TOP_MARGIN,
      left: isCard ? CARD_MARGIN : LEFT_MARGIN,
      bottom: isCard ? CARD_MARGIN : BOTTOM_MARGIN,
      right: isCard ? CARD_MARGIN : RIGHT_MARGIN
    };

    const svg = d3
      .select(`#svg-${this.id}`)
      .attr('viewBox', [0, 0, width, height])
      .style('overflow', 'visible');

    const data = {
      names: dataNamed,
      series: map(numericName, (name, index) => ({
        name,
        values: get(dataNumeric, [`${index}`], [])
      }))
    };

    const x = d3
      .scaleBand()
      .domain(data.names)
      .range([margin.left, width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([
        Math.min(
          0,
          d3.min(data.series, d => d3.min(d.values))
        ),
        d3.max(data.series, d => d3.max(d.values))
      ])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const line = d3
      .line()
      .curve(d3.curveLinear)
      .defined(d => isNumber(d))
      .x((d, i) => x(data.names[i]) + x.step() / 2)
      .y(d => y(d));

    const xAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisBottom(x)
          .tickSizeOuter(0) /* eslint-enable indent */
      : d3.axisBottom(x).tickValues([]);

    const yAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisLeft(y)
          .ticks() /* eslint-enable indent */
      : d3.axisLeft(y).tickValues([]);

    const path = svg
      .append('g')
      .attr('class', `${this.id}`)
      .selectAll('path')
      .data(data.series)
      .join('path')
      .attr('class', `${this.id}`)
      .attr('fill', 'none')
      .attr('stroke', (d, i) => colorRange[i])
      .attr('stroke-opacity', (d, i) => alpha[i])
      .attr('stroke-width', (d, i) => weight[i])
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .style('mix-blend-mode', 'multiply')
      .attr('d', d => line(d.values));

    if (!isCard) {
      svg.call(this.hover, path, data, x, y);

      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(0,${y(0)})`)
        .call(xAxis);

      svg
        .selectAll('.tick')
        .selectAll('text')
        .style('text-anchor', 'start')
        .attr(
          'transform',
          `rotate(${get(
            find(globalProperties, ['name', 'xAxisTextAngle']),
            'value',
            TEXT_INICIAL_SIZE
          )})`
        );

      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(${margin.left},0)`)
        .call(yAxis);

      svg
        .selectAll('.tick')
        .selectAll('text')
        .attr(
          'font-size',
          get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE)
        );

      if (!selectedElements.Axis) {
        svg.selectAll('.domain').remove();
      }
    }
  };

  hover = (svg, path, data, x, y) => {
    const dot = svg
      .append('g')
      .attr('class', `${this.id}`)
      .attr('display', 'none');

    dot.append('circle').attr('r', 2.5);

    dot
      .append('text')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'middle')
      .attr('y', -8);

    svg
      .on('mousemove', () => this.moved(path, dot, data, x, y))
      .on('mouseenter', () => this.entered(path, dot))
      .on('mouseleave', () => this.left(path, dot));
  };

  entered(path, dot) {
    path.style('mix-blend-mode', null).attr('stroke', '#ddd');
    dot.attr('display', null);
  }

  left(path, dot) {
    const { colorRange } = this.props;

    path.style('mix-blend-mode', 'multiply').attr('stroke', (d, i) => colorRange[i]);
    dot.attr('display', 'none');
  }

  moved(path, dot, data, x, y) {
    const { colorRange } = this.props;

    dot.attr('display', null);

    const xAux = d3
      .scaleLinear()
      .domain([0, data.names.length])
      .range(x.range());
    const auxArray = map(data.names, (d, i) => i);

    d3.event.preventDefault();
    const ym = y.invert(d3.event.layerY);
    const xm = xAux.invert(d3.event.layerX);
    const i1 = d3.bisectLeft(auxArray, xm, 1);
    const i0 = i1 - 1;
    const i = xm - (auxArray[i0] + x.step() / 2) > auxArray[i1] + x.step() / 2 - xm ? i1 : i0;
    const s = least(data.series, d => Math.abs(d.values[i] - ym));
    path
      .attr('stroke', (d, index) => (d === s ? colorRange[index] : '#ddd'))
      .filter(d => d === s)
      .raise();
    dot.attr('transform', `translate(${x(data.names[i]) + x.step() / 2},${y(s.values[i])})`);
    dot.select('text').text(s.name);
  }
}

MultiLineChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: ['A', 'B', 'C'],
  dataNumeric: [
    [0, 1, 3, 1.5, 2.5],
    [0.3, 2, 5, 2, 3],
    [0.5, 4, 7, 2.5, 3.5]
  ],
  colorRange: ['rgb(200, 74, 75)', 'rgb(200, 118, 75)', 'rgb(200, 118, 117)'],
  alpha: [0.9, 0.9, 0.9],
  weight: [2, 2, 2],
  numericName: ['AtributoA', 'AtributoB', 'AtributoC'],
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  globalProperties: [],
  isCard: false
};

MultiLineChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNamed: PropTypes.array,
  dataNumeric: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  colorRange: PropTypes.arrayOf(PropTypes.string),
  alpha: PropTypes.arrayOf(PropTypes.number),
  weight: PropTypes.arrayOf(PropTypes.number),
  selectedElements: PropTypes.object,
  globalProperties: PropTypes.array,
  numericName: PropTypes.arrayOf(PropTypes.string),
  isCard: PropTypes.bool
};
export default MultiLineChart;
